import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  schemas,
  type CatalogLightingDetails,
  type CreateCatalogLightingRequest,
} from '@kreart/shared';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Form, TextField, SelectField, CheckboxField } from '@/components/form';
import { Button } from '@/components/ui/button';

interface LightingFormValues {
  name: string;
  offerName: string;
  sku: string;
  internalCode: string;
  manufacturer: string;
  pricePerPiece: string;
  currency: 'RON' | 'EUR' | 'USD';
  active: boolean;
}

const EMPTY: LightingFormValues = {
  name: '',
  offerName: '',
  sku: '',
  internalCode: '',
  manufacturer: '',
  pricePerPiece: '',
  currency: 'RON',
  active: true,
};

const CURRENCY_OPTIONS = [
  { value: 'RON', label: 'RON' },
  { value: 'EUR', label: 'EUR' },
  { value: 'USD', label: 'USD' },
];

interface Props {
  open: boolean;
  onClose: () => void;
  initial: CatalogLightingDetails | null;
  onSubmit: (body: CreateCatalogLightingRequest) => Promise<void>;
}

export function LightingEditDrawer({ open, onClose, initial, onSubmit }: Props) {
  const { t } = useTranslation();
  const form = useForm<LightingFormValues>({
    resolver: zodResolver(schemas.createCatalogLightingRequest),
    defaultValues: EMPTY,
  });

  useEffect(() => {
    if (!open) return;
    if (!initial) {
      form.reset(EMPTY);
      return;
    }
    form.reset({
      name: initial.name,
      offerName: initial.offerName ?? '',
      sku: initial.sku ?? '',
      internalCode: initial.internalCode ?? '',
      manufacturer: initial.manufacturer ?? '',
      pricePerPiece: initial.pricePerPiece ?? '',
      currency: initial.currency,
      active: initial.active,
    });
  }, [open, initial, form]);

  const onValid = async (values: LightingFormValues) => {
    await onSubmit({
      name: values.name,
      offerName: values.offerName || undefined,
      sku: values.sku || undefined,
      internalCode: values.internalCode || undefined,
      manufacturer: values.manufacturer || undefined,
      pricePerPiece: values.pricePerPiece,
      currency: values.currency,
      active: values.active,
    });
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-[min(95vw,520px)] bg-background p-4">
        <SheetTitle className="text-lg font-semibold">
          {initial
            ? t('lightingAdmin.edit', { defaultValue: 'Edit lighting' })
            : t('lightingAdmin.create', { defaultValue: 'New lighting' })}
        </SheetTitle>
        <Form form={form} onSubmit={onValid} className="mt-4 space-y-3">
          <TextField name="name" label={t('common.name', { defaultValue: 'Name' })} />
          <TextField
            name="offerName"
            label={t('catalog.offerName', { defaultValue: 'Offer Name' })}
          />
          <div className="grid grid-cols-2 gap-3">
            <TextField name="internalCode" label="Cod Intern" />
            <TextField name="sku" label="Cod Produs" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <TextField
              name="manufacturer"
              label={t('common.manufacturer', { defaultValue: 'Manufacturer' })}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <TextField
              name="pricePerPiece"
              label={t('lightingAdmin.pricePerPiece', { defaultValue: 'Price / piece' })}
            />
            <SelectField
              name="currency"
              label={t('common.currency', { defaultValue: 'Currency' })}
              options={CURRENCY_OPTIONS}
            />
          </div>
          <CheckboxField name="active" label={t('common.active', { defaultValue: 'Active' })} />
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button type="submit">{t('common.save', { defaultValue: 'Save' })}</Button>
          </div>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
